import React, { useState, useEffect } from "react";

// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  fetchOrder,
  editOrder,
  previewOrderChanges,
  sendEmail,
  sendCancellationEmail,
} from "../../../redux/features/sales/ordersSlice";
import { createCharge } from "../../../redux/features/sales/chargesSlice";
import { createRefund } from "../../../redux/features/sales/refundsSlice";
import {
  createPaymentMethod,
  fetchPaymentMethods,
} from "../../../redux/features/sales/paymentMethodsSlice";
import {
  createAddress,
  fetchAddresses,
} from "../../../redux/features/sales/addressesSlice";

// Routing
import { useParams, useHistory, Redirect } from "react-router-dom";

// Styling
import { makeStyles } from "@material-ui/core/styles";

// Components
import TransactionsTableComponent, {
  getRefundableAmount,
} from "./TransactionsTableComponent";
import CreateRefundDialog from "../Charges/Refunds/CreateRefundDialog";
import RatesTableComponent from "./RatesTableComponent";
import ProductsTableComponent from "./ProductsTableComponent";
import OrderHeader from "./OrderHeader";
import PaymentPanel, { PaymentStatus } from "./PaymentPanel";
import SectionTitle, { ActionButton } from "./SectionTitle";
import { StayPanel, CondoStayPanel } from "./StayPanel";
import OrderStickyBar from "./OrderStickyBar";
import Grid from "@material-ui/core/Grid";
import Paper from "../../UI/MainPaper";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import FullContainerProgress from "../../UI/FullContainerProgress";
import Breadcrumbs from "../../Navigation/Breadcrumbs";
import Box from "@material-ui/core/Box";
import ProductsTableEdit from "../../FormFields/TableInputs/ProductsTableEdit";
import RatesTableEdit from "../../FormFields/TableInputs/RatesTableEdit";

// Icons
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import ChargeCreateDialog from "./ChargeCreateDialog";
import { Elements } from "react-stripe-elements";

const useStyles = makeStyles((theme) => ({
  paperContainer: {
    padding: theme.spacing(3),
    position: "relative",
    minHeight: "100px",
    height: "100%",
  },
}));

const formatPeopleForEdit = (people = []) => {
  const defaultPeople = {
    adults: 1,
    children: 0,
    seniors: 0,
    babies: 0,
  };

  if (!Array.isArray(people) || people.length === 0) {
    return defaultPeople;
  }

  const formattedPeople = people.reduce(
    (acc, person) => {
      const identifier = String(person.identifier || "").toLowerCase();

      if (identifier === "adult" || identifier === "adults") {
        acc.adults = Number(person.qty) || 0;
      }
      if (identifier === "child" || identifier === "children") {
        acc.children = Number(person.qty) || 0;
      }
      if (identifier === "senior" || identifier === "seniors") {
        acc.seniors = Number(person.qty) || 0;
      }
      if (identifier === "baby" || identifier === "babies") {
        acc.babies = Number(person.qty) || 0;
      }

      return acc;
    },
    {
      adults: 0,
      children: 0,
      seniors: 0,
      babies: 0,
    }
  );

  if (formattedPeople.seniors === 0 && formattedPeople.adults === 0) {
    formattedPeople.adults = 1;
  }

  return formattedPeople;
};

// One part of the order page. Sections get more space between them than the
// cards inside a section, so the page reads as distinct groups.
const Section = ({ id, children }) => (
  <Grid item xs={12}>
    {/* scrollMarginTop keeps jump-link targets clear of the sticky bars */}
    <Box mb={3} id={id} style={{ scrollMarginTop: 140 }}>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  </Grid>
);

const OrdersView = () => {
  // Styles
  const classes = useStyles();

  // Local State
  const [isEditProductsMode, setEditProductsMode] = useState(false);
  const [isEditRatesMode, setEditRatesMode] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openSnackbarEmail, setOpenSnackbarEmail] = useState(false);
  const [openSnackbarCancellationEmail, setOpenSnackbarCancellationEmail] =
    useState(false);
  const [
    snackbarContentCancellationEmail,
    setSnackbarContentCancellationEmail,
  ] = useState("");
  const [openChargeDialog, setOpenChargeDialog] = useState(false);
  // Transaction being refunded from the Transactions table, if any
  const [refundTransaction, setRefundTransaction] = useState(null);
  const [snackbarContent, setSnackbarContent] = useState("");
  const [snackbarContentEmail, setSnackbarContentEmail] = useState("");

  // Routing
  const { id } = useParams();
  const history = useHistory();

  // Redux
  const dispatch = useDispatch();
  const {
    currentPageOrder,
    isLoading,
    error,
    isLoadingEmail,
    errorEmail,
    isLoadingCancellationEmail,
    errorCancellationEmail,
  } = useSelector((state) => state.orders);

  const orderInfo = {
    check_in: currentPageOrder.check_in,
    check_out: currentPageOrder.check_out,
    package_name: currentPageOrder.package?.package_name,
    room_id: currentPageOrder.package?.originals.room_id,
    hotel_id: currentPageOrder.package?.originals.hotel_id,
    room_qty: currentPageOrder.package?.room_qty,
    rates: currentPageOrder.rates,
    people: formatPeopleForEdit(currentPageOrder.people),
    coupon_id: currentPageOrder.coupon?.coupon_id ?? null,
  };

  let breadcrumbNameMap = {
    "/sales": "Sales",
    "/sales/orders": "Orders",
  };
  // No crumb for the order itself: the page title already shows "Order #id"

  const isPackage = currentPageOrder.type === "package";
  const openChargeDialogHandler = () => setOpenChargeDialog(true);

  // The order whose first load has finished. Later refetches (after a save)
  // keep the page on screen instead of swapping it for a spinner.
  const [loadedOrderId, setLoadedOrderId] = useState(null);

  useEffect(() => {
    // check if the id is a int to keep going
    // This if is why javascript is broken :)
    if (Number.isInteger(id * 1) && id * 1 < Number.MAX_SAFE_INTEGER) {
      let isCurrent = true;
      dispatch(fetchOrder(id)).then(() => {
        if (isCurrent) setLoadedOrderId(id);
      });
      return () => {
        isCurrent = false;
      };
    } else {
      history.push("/404");
    }
  }, [dispatch, history, id]);

  // Opening an editor scrolls it into view (under the sticky bars) and puts the
  // cursor in its first field, so it's obvious where editing happens.
  useEffect(() => {
    if (!isEditRatesMode) return;
    const section = document.getElementById("order-booking");
    if (!section) return;

    section.scrollIntoView({ behavior: "smooth", block: "start" });
    const firstField = section.querySelector('input[name="package_name"]');
    if (firstField) firstField.focus({ preventScroll: true });
  }, [isEditRatesMode]);

  useEffect(() => {
    if (!isEditProductsMode) return;
    const section = document.getElementById("order-products");
    if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [isEditProductsMode]);

  const handleClose = () => {
    setOpenSnackbar(false);
    setSnackbarContent("");
  };

  const handleCloseEmail = () => {
    setOpenSnackbarEmail(false);
    setSnackbarContentEmail("");
  };

  const handleCloseCancellationEmail = () => {
    setOpenSnackbarCancellationEmail(false);
    setSnackbarContentCancellationEmail("");
  };

  const buildProductsOrderObj = (values) => ({
    products: values.map(
      ({
        name,
        price,
        tax,
        type,
        quantity,
        age_restriction,
        product_type,
      }) => ({
        name,
        price,
        tax,
        type,
        quantity,
        age_restriction,
        product_type,
      })
    ),
  });

  const onProductsPreview = (values) =>
    dispatch(previewOrderChanges(id, buildProductsOrderObj(values)));

  const onProductsSubmit = async (values) => {
    // Logic to show error or success
    const res = await dispatch(editOrder(id, buildProductsOrderObj(values)));
    if (!res.error) {
      dispatch(fetchOrder(id));
      setSnackbarContent(
        "Your changes to the Order Products were saved successfully!"
      );
      setOpenSnackbar(true);
      setEditProductsMode(false);
      return true;
    }

    setSnackbarContent(
      "There was an error updating the values. Please try again."
    );
    setOpenSnackbar(true);
    return false;
  };

  const onStatusSubmit = async (status) => {
    const orderObj = {
      status,
    };

    // Logic to show error or success
    const res = await dispatch(editOrder(id, orderObj));
    if (!res.error) {
      dispatch(fetchOrder(id));
      setSnackbarContent(
        "Your changes to the Order Status were saved successfully!"
      );
      setOpenSnackbar(true);
      setEditRatesMode(false);
      return true;
    }

    setSnackbarContent(
      "There was an error updating the values. Please try again."
    );
    setOpenSnackbar(true);
    return false;
  };

  const onReservationStatusSubmit = async (reservation_status) => {
    const orderObj = {
      reservation_status,
    };

    // Logic to show error or success
    const res = await dispatch(editOrder(id, orderObj));
    if (!res.error) {
      dispatch(fetchOrder(id));
      setSnackbarContent(
        "Your changes to the Reservation Status were saved successfully!"
      );
      setOpenSnackbar(true);
      setEditRatesMode(false);
    } else {
      setSnackbarContent(
        "There was an error updating the values. Please try again."
      );
      setOpenSnackbar(true);
    }
  };

  const onSendEmailSubmit = async (emailObj) => {
    // Logic to show error or success
    const res = await dispatch(sendEmail(id, emailObj));
    if (!res.error) {
      setSnackbarContentEmail("Your email(s) were sent successfully!");
      setOpenSnackbarEmail(true);
      setEditRatesMode(false);
    } else {
      setSnackbarContentEmail(
        "There was an error sending the emails. Please try again."
      );
      setOpenSnackbarEmail(true);
    }
  };

  const onSendCancellationEmailSubmit = async () => {
    // Logic to show error or success
    const res = await dispatch(sendCancellationEmail(id));
    if (!res.error) {
      setSnackbarContentCancellationEmail(
        "The cancellation email was sent successfully!"
      );
      setOpenSnackbarCancellationEmail(true);
    } else {
      setSnackbarContentCancellationEmail(
        "There was an error sending the cancellation email. Please try again."
      );
      setOpenSnackbarCancellationEmail(true);
    }
  };

  const onCustomerSubmit = async (customer_id) => {
    const orderObj = {
      customer_id,
    };

    // Logic to show error or success
    const res = await dispatch(editOrder(id, orderObj));
    if (!res.error) {
      dispatch(fetchOrder(id));
      setSnackbarContent(
        "Your changes to the Order Customer were saved successfully!"
      );
      setOpenSnackbar(true);
      setEditRatesMode(false);
    } else {
      setSnackbarContent(
        "There was an error updating the values. Please try again."
      );
      setOpenSnackbar(true);
    }
  };

  const buildRatesOrderObj = (values) => {
    const { rates, people, coupon_id, ...info } = values;

    return {
      info,
      people,
      rates,
      ...(coupon_id !== undefined ? { coupon_id } : {}),
    };
  };

  const onRatesPreview = (values) =>
    dispatch(previewOrderChanges(id, buildRatesOrderObj(values)));

  const onRatesSubmit = async (values) => {
    // Logic to show error or success
    const res = await dispatch(editOrder(id, buildRatesOrderObj(values)));
    if (!res.error) {
      dispatch(fetchOrder(id));
      setSnackbarContent(
        "Your changes to the Order Rates were saved successfully!"
      );
      setOpenSnackbar(true);
      setEditRatesMode(false);
      return true;
    }

    setSnackbarContent(
      "There was an error updating the values. Please try again."
    );
    setOpenSnackbar(true);
    return false;
  };

  const onRefundSubmit = async (values) => {
    const res = await dispatch(
      createRefund(refundTransaction.transaction_id, values)
    );
    if (!res.error) {
      setRefundTransaction(null);
      dispatch(fetchOrder(id));
      setSnackbarContent("The refund was created successfully!");
      setOpenSnackbar(true);
    }
  };

  const onChargeSubmit = async (values) => {
    const { order_id, amount, card_id, newPaymentMethod } = values;

    let resolvedCardId = card_id;

    if (newPaymentMethod) {
      const { payment_method_id, newAddress } = newPaymentMethod;
      const customerId = currentPageOrder.customer_id;

      if (newAddress) {
        const addressRes = await dispatch(
          createAddress(customerId, newAddress)
        );
        if (addressRes.error) return;
        dispatch(fetchAddresses(customerId));
      }

      const pmRes = await dispatch(
        createPaymentMethod(customerId, { payment_method_id })
      );
      if (pmRes.error) return;
      dispatch(fetchPaymentMethods(customerId));
      resolvedCardId = pmRes.id;
    }

    const res = await dispatch(
      createCharge({ order_id, amount, card_id: resolvedCardId })
    );
    if (!res.error) {
      dispatch(fetchOrder(id));
      setSnackbarContent("Your charge was done successfully!");
      setOpenSnackbar(true);
      setEditProductsMode(false);
      setOpenChargeDialog(false);
    }
  };

  // Redirect if order doesn't exist'
  if (error && error.errorCode === 404) {
    return <Redirect to="/404" />;
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid container item xs={12} alignItems="center">
          <Breadcrumbs breadcrumbNameMap={breadcrumbNameMap} />
        </Grid>

        {loadedOrderId !== id || !Object.entries(currentPageOrder).length ? (
          <Grid item xs={12}>
            <Paper className={classes.paperContainer}>
              <FullContainerProgress />
            </Paper>
          </Grid>
        ) : (
          <>
            <OrderStickyBar
              orderId={id}
              order={currentPageOrder}
              watchId="order-overview"
              sections={[
                { id: "order-overview", label: "Overview" },
                { id: "order-booking", label: "Booking" },
                ...(isPackage
                  ? [{ id: "order-products", label: "Products" }]
                  : []),
                { id: "order-payments", label: "Payments" },
              ]}
            />

            <Section id="order-overview">
              <Grid item xs={12}>
                <OrderHeader
                  orderId={id}
                  order={currentPageOrder}
                  onStatusSubmit={onStatusSubmit}
                  onReservationStatusSubmit={onReservationStatusSubmit}
                  onCustomerSubmit={onCustomerSubmit}
                  onSendEmailSubmit={onSendEmailSubmit}
                  isLoadingEmail={isLoadingEmail}
                  onSendCancellationEmailSubmit={onSendCancellationEmailSubmit}
                  isLoadingCancellationEmail={isLoadingCancellationEmail}
                />
              </Grid>
            </Section>

            <Section>
              {/* Payment status at a glance */}
              <Grid item xs={12}>
                <PaymentStatus
                  amounts={currentPageOrder.amounts}
                  type={currentPageOrder.type}
                  charge_remaining_by={currentPageOrder.charge_remaining_by}
                  onAddCharge={openChargeDialogHandler}
                />
              </Grid>
            </Section>

            <Section id="order-booking">
              {/* Booking details: editing replaces the card and rates table */}
              {isPackage && isEditRatesMode && (
                <Grid item xs={12}>
                  <Paper className={classes.paperContainer}>
                    <Box mb={3}>
                      <SectionTitle
                        spread
                        title="Edit Booking Details"
                        action={
                          <ActionButton
                            tone="neutral"
                            icon={<CloseIcon />}
                            onClick={() => setEditRatesMode(false)}
                          >
                            Cancel Edit
                          </ActionButton>
                        }
                      />
                    </Box>
                    <RatesTableEdit
                      orderInfo={orderInfo}
                      currentAmounts={currentPageOrder.amounts}
                      currentHotelName={currentPageOrder.package?.hotel_name}
                      currentRoomName={currentPageOrder.package?.room_name}
                      currentCouponName={currentPageOrder.coupon?.name}
                      packageId={currentPageOrder.package?.originals.package_id}
                      onPreview={onRatesPreview}
                      onSubmit={onRatesSubmit}
                    />
                  </Paper>
                </Grid>
              )}

              {isPackage && currentPageOrder.package && !isEditRatesMode && (
                <Grid item xs={12} lg={5}>
                  <StayPanel
                    packageObj={currentPageOrder.package}
                    peopleArr={currentPageOrder.people}
                    check_in={currentPageOrder.check_in}
                    check_out={currentPageOrder.check_out}
                    coupon={currentPageOrder.coupon}
                    onEdit={() => setEditRatesMode(true)}
                  />
                </Grid>
              )}
              {currentPageOrder.type === "condo" && currentPageOrder.condo && (
                <Grid item xs={12} lg={5}>
                  <CondoStayPanel
                    condoObj={currentPageOrder.condo}
                    peopleArr={currentPageOrder.people}
                    check_in={currentPageOrder.check_in}
                    check_out={currentPageOrder.check_out}
                    coupon={currentPageOrder.coupon}
                  />
                </Grid>
              )}
              {isPackage && !isEditRatesMode && (
                <Grid item xs={12} lg={7}>
                  <RatesTableComponent
                    isLoading={isLoading}
                    ratesArray={currentPageOrder.rates}
                    roomQty={Number(currentPageOrder.package?.room_qty) || 1}
                  />
                </Grid>
              )}
            </Section>

            {isPackage && (
              <Section id="order-products">
                {/* Products */}
                <Grid item xs={12}>
                  {isEditProductsMode ? (
                    <ProductsTableEdit
                      originalData={currentPageOrder.products}
                      currentAmounts={currentPageOrder.amounts}
                      onPreview={onProductsPreview}
                      onSubmit={onProductsSubmit}
                      headerAction={
                        <ActionButton
                          tone="neutral"
                          icon={<CloseIcon />}
                          onClick={() => setEditProductsMode(false)}
                        >
                          Cancel Edit
                        </ActionButton>
                      }
                    />
                  ) : (
                    <ProductsTableComponent
                      isLoading={isLoading}
                      productsArray={currentPageOrder.products}
                      headerAction={
                        <ActionButton
                          icon={<EditIcon />}
                          onClick={() => setEditProductsMode(true)}
                        >
                          Edit
                        </ActionButton>
                      }
                    />
                  )}
                </Grid>
              </Section>
            )}

            <Section id="order-payments">
              {/* Payment details and the charges made against the order */}
              <Grid item xs={12} lg={5}>
                <PaymentPanel
                  amounts={currentPageOrder.amounts}
                  type={currentPageOrder.type}
                  onAddCharge={openChargeDialogHandler}
                />
              </Grid>
              <Grid item xs={12} lg={7}>
                <TransactionsTableComponent
                  isLoading={isLoading}
                  transactionArray={currentPageOrder.transactions}
                  onRefund={setRefundTransaction}
                  headerAction={
                    <ActionButton
                      icon={<AddIcon />}
                      onClick={openChargeDialogHandler}
                    >
                      Add Charge
                    </ActionButton>
                  }
                />
              </Grid>
            </Section>
          </>
        )}
      </Grid>

      {refundTransaction && (
        <CreateRefundDialog
          key={refundTransaction.transaction_id}
          open
          chargeId={refundTransaction.transaction_id}
          orderId={id}
          maxAmount={getRefundableAmount(refundTransaction)}
          onClose={() => setRefundTransaction(null)}
          onSubmit={onRefundSubmit}
        />
      )}

      <Elements>
        <ChargeCreateDialog
          open={openChargeDialog}
          onClose={() => setOpenChargeDialog(false)}
          onSubmit={onChargeSubmit}
          readOnly={false}
          currentPageOrder={currentPageOrder}
        />
      </Elements>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleClose}
          severity={error ? "error" : "success"}
        >
          {snackbarContent}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={openSnackbarEmail}
        autoHideDuration={6000}
        onClose={handleCloseEmail}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleCloseEmail}
          severity={errorEmail ? "error" : "success"}
        >
          {snackbarContentEmail}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={openSnackbarCancellationEmail}
        autoHideDuration={6000}
        onClose={handleCloseCancellationEmail}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleCloseCancellationEmail}
          severity={errorCancellationEmail ? "error" : "success"}
        >
          {snackbarContentCancellationEmail}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default OrdersView;
